/* eslint-disable no-console */
import { shouldLog } from './log-level';
import { LOGGER_SEVERITY } from './logger-severity';
import { redactPii } from './redact-pii';
import type { SerializeErrorForLogOptions } from './serialize-error-for-log';
import { serializeErrorForLog, serializeErrorsInPayload } from './serialize-error-for-log';
import { shipClientLog } from './ship-client-log';

const SERVER_ERROR_OPTIONS: SerializeErrorForLogOptions = { includeStack: true };
/**
 * Browser logs are relayed by `shipClientLog`, which drops any body over 4 KB. So skip stacks
 * (they are minified anyway), keep messages short and follow one cause.
 */
const BROWSER_ERROR_OPTIONS: SerializeErrorForLogOptions = {
  includeStack: false,
  maxMessageLength: 500,
  maxCauseDepth: 1,
};

export { LOGGER_SEVERITY };
export { configuredLogLevel, parseLogLevel, shouldLog } from './log-level';

type severity = (typeof LOGGER_SEVERITY)[keyof typeof LOGGER_SEVERITY];
interface LoggerParams {
  message: string;
  tag?: string;
  severity?: severity;
  payload?: object;
}

export function logger(params: LoggerParams) {
  try {
    const { message, severity = LOGGER_SEVERITY.LOG, payload = {}, tag = '' } = params;
    if (!shouldLog(severity)) {
      return;
    }
    // Errors become `{}` under JSON.stringify, so flatten them, then redact the result, so
    // redaction sees exactly what is logged. (A serialized error's class is `errorName`, not the
    // `name` PII key.)
    const safePayload = redactPii(
      serializeErrorsInPayload(
        payload,
        typeof window === 'undefined' ? SERVER_ERROR_OPTIONS : BROWSER_ERROR_OPTIONS,
      ),
    );
    const msgString = tag ? `[${tag}] ${message}` : message;

    if (process.env.NODE_ENV === 'development') {
      const hhmmss = new Date().toISOString().slice(11, 19);
      // pretty string for terminal output
      console.log(
        `[${hhmmss} ${severity}] [${tag}] ${message}; payload: ${JSON.stringify(safePayload)}`,
      );
      return;
    }

    // structured log for console
    const log = JSON.stringify({ severity, payload: safePayload, message: msgString, tag });

    switch (severity) {
      case LOGGER_SEVERITY.ERROR:
        console.error(log);
        break;

      case LOGGER_SEVERITY.WARN:
        console.warn(log);
        break;

      case LOGGER_SEVERITY.DEBUG:
        console.debug(log);
        break;

      default:
        console.log(log);
        break;
    }

    if (
      typeof window !== 'undefined' &&
      (severity === LOGGER_SEVERITY.WARN || severity === LOGGER_SEVERITY.ERROR)
    ) {
      shipClientLog({
        severity,
        tag,
        message,
        payload: safePayload,
      });
    }
  } catch (error) {
    console.error(
      JSON.stringify({ message: 'logger - error', error: serializeErrorForLog(error) }),
    );
  }
}

interface CreateLoggersParams {
  tag: string;
}

export function createLoggers(params: CreateLoggersParams) {
  const log = (message: string, payload?: object) => {
    logger({
      severity: LOGGER_SEVERITY.LOG,
      tag: params.tag,
      message,
      payload,
    });
  };

  const debug = (message: string, payload?: object) => {
    logger({
      severity: LOGGER_SEVERITY.DEBUG,
      tag: params.tag,
      message,
      payload,
    });
  };

  const warn = (message: string, payload?: object) => {
    logger({
      severity: LOGGER_SEVERITY.WARN,
      tag: params.tag,
      message,
      payload,
    });
  };

  const error = (message: string, payload?: object) => {
    logger({
      severity: LOGGER_SEVERITY.ERROR,
      tag: params.tag,
      message,
      payload,
    });
  };

  const info = (message: string, payload?: object) => {
    logger({
      severity: LOGGER_SEVERITY.INFO,
      tag: params.tag,
      message,
      payload,
    });
  };

  return {
    error,
    warn,
    debug,
    log,
    info,
  };
}
